/**
 * Undo system initialization — wires the undo middleware + tools into mcpBus.
 *
 * Call initUndo() once during app startup (after registerStudioTools).
 * Safe to call multiple times — subsequent calls are no-ops.
 */

import { mcpBus } from './studio-mcp-bus'
import { createUndoMiddleware, registerUndoTools } from './undo-middleware'
import { syncUndoState } from './undo-state-store'
import type { UndoState } from './undo-types'

let _initialized = false
let _undoState: ReturnType<typeof createUndoMiddleware> | null = null

/** Initialize undo middleware and register undo/redo tools. Safe to call multiple times. */
export function initUndo(): void {
  if (_initialized) return
  _initialized = true

  _undoState = createUndoMiddleware({
    onStackChange: (state) => syncUndoState({ canUndo: state.canUndo, canRedo: state.canRedo }),
  })
  mcpBus.use(_undoState.middleware)
  registerUndoTools(mcpBus, _undoState)

  console.log('[init-undo] Undo middleware and tools registered')
}

/** Get current undo state (canUndo, canRedo, stackSize, estimatedBytes). */
export function getUndoState(): UndoState {
  return _undoState?.getState() ?? { canUndo: false, canRedo: false, stackSize: 0, estimatedBytes: 0 }
}

/**
 * Clear BOTH undo/redo stacks (project-scope reset, PI-3). Called on every
 * project switch via flow-editor-store's `ensureProject` — hydrated flow ids
 * (`<block>:<name>`) collide across template-derived projects, so entries
 * recorded in one project must never be replayable in another. No-op before
 * initUndo().
 */
export function clearUndoHistory(): void {
  _undoState?.clearAll()
}
